'use client';

import Image from "next/image";
import React from "react";
import { m, LazyMotion, domAnimation } from "framer-motion";

interface Picture {
  id: number;
  attributes: {
    url: string;
    name: string;
    alternativeText: string | null;
    caption?: string;
  };
}

interface ImageMasonryProps {
  data: {
    id: string;
    title: string;
    subtitle: string;
    images: {
      data: Picture[];
    };
    isLive: boolean;
    isReversed: boolean;
    date: string;
    categorySlug: string;
    useH1Tag?: boolean;
    slug: string;
  };
}

export default function ImageMasonry_Layout1({ data }: ImageMasonryProps) {
  if (!data.isLive) return null;

  const HeadingTag = data.useH1Tag ? "h1" : "h2";

  return (
    <LazyMotion features={domAnimation}>
      <section className="relative py-16 bg-white text-black overflow-hidden">
        {/* Header */}
        <div className="max-w-6xl mx-auto px-6 text-center mb-16">
          <HeadingTag className="text-4xl md:text-5xl   mb-4 font-bold">
            {data.title}
          </HeadingTag>
          {data.subtitle && (
            <p className="text-lg text-gray-700 max-w-2xl mx-auto font-montserrat">
              {data.subtitle}
            </p>
          )}
        </div>

        {/* Full-width Masonry Grid with side gaps */}
        <div className="w-full px-6">
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 space-y-6">
            {data.images.data.map((image, index) => (
              <m.div
                key={image.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-20%" }}
                transition={{ duration: 0.7, delay: index * 0.15 }}
                className="relative w-full break-inside-avoid rounded-xl overflow-hidden shadow-lg bg-white transform transition-all duration-300 hover:shadow-xl"
              >
                {/* Image Container */}
                <div className="relative w-full overflow-hidden">
                  <Image
                    src={image.attributes.url || "/placeholder-denim.jpg"}
                    alt={image.attributes.alternativeText || "Denim Fashion"}
                    width={500}
                    height={650}
                    className="w-full h-auto object-cover"
                  />
                  
                  {/* Overlay Content - Always Visible */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent flex items-end">
                    <div className="w-full p-5 text-white">
                      <h3 className="text-xl font-bold mb-2">Premium Denim</h3>
                      <p className="text-sm mb-3">
                        {image.attributes.caption || "Crafted with quality materials for perfect fit and comfort"}
                      </p>
                      <div className="flex justify-between items-center">
                        <button className="px-4 py-2 bg-white text-black text-xs font-semibold rounded-full transition-all duration-300 hover:bg-gray-100 hover:scale-105">
                          Shop Now
                        </button>
                        <span className="text-xs font-medium">${79 + index * 10}.00</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Category Badge */}
                  <div className="absolute top-4 left-4 px-3 py-1 bg-white rounded-full text-black text-xs font-bold">
                    {index % 3 === 0 ? "Jeans" : index % 3 === 1 ? "Jacket" : "Shorts"}
                  </div>
                </div>
              </m.div>
            ))}
            
            {/* Add some hardcoded items if needed for better layout */}
            {data.images.data.length % 2 !== 0 && (
              <>
                <div className="relative w-full break-inside-avoid rounded-xl overflow-hidden shadow-lg bg-white">
                  <div className="relative w-full h-0 pb-[130%] bg-gradient-to-br from-blue-900 to-indigo-700 flex items-center justify-center">
                    <div className="text-center p-6 text-white">
                      <h3 className="text-2xl font-bold mb-3">New Collection</h3>
                      <p className="text-sm mb-4">Discover our latest denim designs</p>
                      <button className="px-5 py-2 bg-white text-black text-sm font-semibold rounded-full">
                        View All
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* CTA Section */}
        <div className="max-w-3xl mx-auto px-6 mt-16 text-center">
          <h3 className="text-2xl   font-bold mb-4">Complete Your Denim Look</h3>
          <p className="text-gray-700 mb-6">Mix and match from our collection to create your perfect style</p>
          <button className="px-8 py-3 bg-black text-white font-semibold rounded-full transition-all duration-300 hover:bg-gray-800">
            Explore Full Collection
          </button>
        </div>

        {/* Background Pattern */}
        <div className="absolute inset-0 -z-10 opacity-[0.03]">
          <div className="absolute top-0 left-0 w-full h-full bg-grid-pattern bg-cover"></div>
        </div>
      </section>
    </LazyMotion>
  );
}